// hooks/useAuthFocusSync.js
import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ActionTypes } from "src/store/constants";
import authChannel from "src/helpers/authChannel";
import { GetToken } from "src/helpers/request";

// ============================================
// DEBUGGING UTILITY
// ============================================
const DEBUG = false;

const createLogger = (prefix) => {
  return {
    log: (...args) => DEBUG && console.log(`[${prefix}]`, ...args),
    warn: (...args) => DEBUG && console.warn(`[${prefix}]`, ...args),
    error: (...args) => DEBUG && console.error(`[${prefix}]`, ...args),
    info: (...args) => DEBUG && console.info(`[${prefix}]`, ...args),
    group: (label, fn) => {
      if (DEBUG) {
        console.group(`[${prefix}] ${label}`);
        fn();
        console.groupEnd();
      }
    },
  };
};

export default function useAuthFocusSync(enabled) {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(
    (state) => state.authentication?.isAuthenticated,
  );

  // ===== LOGGER =====
  const logger = createLogger("useAuthFocusSync");

  const isActive = enabled && isAuthenticated;

  // ===== REFS =====
  const lastCallRef = useRef(0);
  const tabIdRef = useRef(`tab_${Math.random().toString(36).slice(2)}`);
  const timeoutIdRef = useRef(null);
  const lastFocusTargetRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoggingOutRef = useRef(false);
  const isCleanedUpRef = useRef(false);
  const eventCountRef = useRef({ focusin: 0, visibility: 0, windowFocus: 0 });

  const handlersRef = useRef({
    focusin: null,
    visibility: null,
    windowFocus: null,
  });

  // ===== STATE =====
  const [isModalActive, setIsModalActive] = useState(false);

  // ============================================
  // CEK APAKAH BOLEH PROSES
  // ============================================
  const canProcess = useCallback(() => {
    const result =
      isMountedRef.current &&
      isAuthenticated &&
      !isLoggingOutRef.current &&
      !isCleanedUpRef.current;

    // Log status setiap kali dicek
    if (!result && DEBUG) {
      const reasons = [];
      if (!isMountedRef.current) reasons.push("!isMounted");
      if (!isAuthenticated) reasons.push("!isAuthenticated");
      if (isLoggingOutRef.current) reasons.push("isLoggingOut");
      if (isCleanedUpRef.current) reasons.push("isCleanedUp");

      if (reasons.length > 0) {
        logger.log("⛔ Cannot process:", reasons.join(", "));
      }
    }

    return result;
  }, [isActive]);

  // ============================================
  // CEK MODAL AKTIF
  // ============================================
  const checkModalActive = useCallback(() => {
    if (!canProcess()) return false;

    try {
      const hasModalClass =
        document.body.classList.contains("ant-modal-open") ||
        document.body.classList.contains("ant-drawer-open");

      if (hasModalClass) {
        logger.log("🔴 Modal/Drawer is active (class)");
        return true;
      }

      const modals = document.querySelectorAll(
        ".ant-modal-wrap, .ant-drawer-wrap",
      );
      for (const el of modals) {
        const style = window.getComputedStyle(el);
        if (style.display !== "none" && style.visibility !== "hidden") {
          logger.log("🔴 Modal/Drawer is active (visible)");
          return true;
        }
      }

      const activeElement = document.activeElement;
      if (
        activeElement?.closest(".ant-modal, .ant-drawer, .ant-modal-content")
      ) {
        logger.log("🔴 Modal/Drawer is active (focus)");
        return true;
      }

      return false;
    } catch (error) {
      logger.error("Error checking modal active:", error);
      return false;
    }
  }, [canProcess]);

  // ============================================
  // CEK APAKAH FOKUS DARI INTERAKSI SAH
  // ============================================
  const isValidFocusTarget = useCallback(
    (target) => {
      if (!target || !canProcess()) return false;

      const interactiveElements = [
        "button",
        "input",
        "select",
        "textarea",
        "a",
        '[role="button"]',
        '[role="link"]',
        '[role="checkbox"]',
        '[role="radio"]',
        '[role="tab"]',
        '[role="menuitem"]',
      ];

      for (const selector of interactiveElements) {
        try {
          if (target.matches(selector) || target.closest(selector)) {
            logger.log("⏭️ Skip: interactive element", selector);
            return false;
          }
        } catch {
          // Skip jika selector invalid
        }
      }

      if (
        lastFocusTargetRef.current === target &&
        document.activeElement === target
      ) {
        logger.log("⏭️ Skip: same target");
        return false;
      }

      return true;
    },
    [canProcess],
  );

  // ============================================
  // TRIGGER PROFILE UPDATE
  // ============================================
  const triggerProfileUpdate = useCallback(
    (source = "self") => {
      const token = GetToken();
      // 🔥 CEK: Jangan trigger jika tidak boleh proses
      if (!canProcess()) {
        logger.log("⏭️ Skip profile update: cannot process");
        return;
      }

      if (checkModalActive()) {
        logger.log("⏭️ Skip profile update: modal active");
        return;
      }

      const now = Date.now();
      if (now - lastCallRef.current < 10000) {
        logger.log(
          `⏭️ Skip profile update: throttled (${now - lastCallRef.current}ms < 5000ms)`,
        );
        return;
      }
      lastCallRef.current = now;

      logger.log(`🔄 Trigger profile update from: ${source}`);
      logger.log(`📊 Tab ID: ${tabIdRef.current}`);

      if (!token?.token) return;
      console.log(token);

      try {
        dispatch({ type: ActionTypes.PROFILE_UPDATE });
        logger.log("✅ Dispatch PROFILE_UPDATE success");
      } catch (error) {
        logger.error("❌ Dispatch PROFILE_UPDATE failed:", error);
      }

      if (source === "self") {
        try {
          authChannel.postMessage({
            type: "PROFILE_UPDATE",
            from: tabIdRef.current,
          });
          logger.log("✅ Broadcast to other tabs success");
        } catch (error) {
          logger.error("❌ Failed to post message:", error);
        }
      }
    },
    [dispatch, canProcess, checkModalActive],
  );

  // ============================================
  // HANDLER DENGAN VALIDASI LEBIH KETAT
  // ============================================
  const handleTrigger = useCallback(
    (event) => {
      // 🔥 CEK: Jangan proses jika tidak boleh proses
      if (!canProcess()) {
        return;
      }

      if (event && event.target) {
        if (!isValidFocusTarget(event.target)) {
          return;
        }
      }

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      timeoutIdRef.current = setTimeout(() => {
        // 🔥 CEK LAGI: Jangan proses jika tidak boleh proses
        if (!canProcess()) {
          return;
        }

        if (document.visibilityState === "visible" && !checkModalActive()) {
          const activeElement = document.activeElement;
          if (activeElement && isValidFocusTarget(activeElement)) {
            logger.log("🎯 Focus trigger after 500ms delay");
            triggerProfileUpdate("self");
          }
        }
      }, 500);
    },
    [canProcess, isValidFocusTarget, checkModalActive, triggerProfileUpdate],
  );

  // ============================================
  // HANDLER KHUSUS UNTUK FOKUS DARI WINDOW
  // ============================================
  const handleWindowFocus = useCallback(() => {
    // 🔥 CEK: Jangan proses jika tidak boleh proses
    if (!canProcess()) {
      return;
    }

    logger.log("🪟 Window focus event");

    if (document.hasFocus()) {
      setTimeout(() => {
        // 🔥 CEK LAGI
        if (!canProcess()) {
          return;
        }

        const activeElement = document.activeElement;
        if (
          activeElement === document.body ||
          activeElement === document.documentElement ||
          !activeElement
        ) {
          if (!checkModalActive()) {
            logger.log("🪟 Window focus trigger (no active element)");
            triggerProfileUpdate("self");
          }
        } else {
          logger.log("🪟 Window focus ignored (active element exists)");
        }
      }, 100);
    }
  }, [canProcess, checkModalActive, triggerProfileUpdate]);

  // ============================================
  // CLEANUP LISTENERS - LENGKAP
  // ============================================
  const cleanupListeners = useCallback(() => {
    // 🔥 Cegah cleanup ganda
    if (isCleanedUpRef.current) {
      logger.log("⏭️ Already cleaned up");
      return;
    }

    logger.group("🧹 Cleaning up event listeners", () => {
      logger.log(`📊 Event stats:`, {
        focusin: eventCountRef.current.focusin,
        visibility: eventCountRef.current.visibility,
        windowFocus: eventCountRef.current.windowFocus,
      });
    });

    isCleanedUpRef.current = true;

    // Clear all timeouts
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
      logger.log("✅ Timeout cleared");
    }

    // Remove all event listeners
    const { focusin, visibility, windowFocus } = handlersRef.current;

    if (focusin) {
      try {
        document.removeEventListener("focusin", focusin, { capture: true });
        logger.log("✅ focusin listener removed");
      } catch (e) {
        logger.warn("Error removing focusin listener:", e);
      }
      handlersRef.current.focusin = null;
    }

    if (visibility) {
      try {
        document.removeEventListener("visibilitychange", visibility);
        logger.log("✅ visibilitychange listener removed");
      } catch (e) {
        logger.warn("Error removing visibility listener:", e);
      }
      handlersRef.current.visibility = null;
    }

    if (windowFocus) {
      try {
        window.removeEventListener("focus", windowFocus);
        logger.log("✅ window focus listener removed");
      } catch (e) {
        logger.warn("Error removing window focus listener:", e);
      }
      handlersRef.current.windowFocus = null;
    }

    // Reset refs
    lastCallRef.current = 0;
    lastFocusTargetRef.current = null;
    isLoggingOutRef.current = true;
    isMountedRef.current = false;
    logger.log("✅ All refs reset");
  }, []);

  // ============================================
  // CEK APAKAH LOGOUT - PRIORITAS TINGGI
  // ============================================
  useEffect(() => {
    if (!isAuthenticated) {
      logger.log("🚪 User logged out - IMMEDIATE CLEANUP");

      // 🔥 CLEANUP SEGERA
      isLoggingOutRef.current = true;
      isCleanedUpRef.current = true;

      // Clear semua timeout
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
        logger.log("✅ Timeout cleared on logout");
      }

      // Remove semua event listener
      const { focusin, visibility, windowFocus } = handlersRef.current;

      if (focusin) {
        try {
          document.removeEventListener("focusin", focusin, { capture: true });
          logger.log("✅ focusin listener removed on logout");
        } catch (e) {}
        handlersRef.current.focusin = null;
      }

      if (visibility) {
        try {
          document.removeEventListener("visibilitychange", visibility);
          logger.log("✅ visibilitychange listener removed on logout");
        } catch (e) {}
        handlersRef.current.visibility = null;
      }

      if (windowFocus) {
        try {
          window.removeEventListener("focus", windowFocus);
          logger.log("✅ window focus listener removed on logout");
        } catch (e) {}
        handlersRef.current.windowFocus = null;
      }

      // Reset refs
      lastCallRef.current = 0;
      lastFocusTargetRef.current = null;
      isMountedRef.current = false;
      logger.log("✅ All refs reset on logout");

      // Reset event counter
      eventCountRef.current = { focusin: 0, visibility: 0, windowFocus: 0 };
    } else {
      // Reset flag saat login
      isLoggingOutRef.current = false;
      isCleanedUpRef.current = false;
      isMountedRef.current = true;
      logger.log("✅ User logged in, enabling sync");
    }
  }, [isAuthenticated]);

  // ============================================
  // EFFECT: SETUP
  // ============================================
  useEffect(() => {
    // 🔥 Jika tidak authenticated, jangan setup apapun
    if (!isAuthenticated) {
      logger.log("⏭️ Skip setup: not authenticated");
      return;
    }

    // Reset flag
    isCleanedUpRef.current = false;
    isMountedRef.current = true;
    isLoggingOutRef.current = false;

    logger.group("🔧 Setting up auth focus sync", () => {
      logger.log(`📊 Tab ID: ${tabIdRef.current}`);
      logger.log(`📊 Authentication: ${isAuthenticated}`);
      logger.log(`📊 Environment: ${process.env.NODE_ENV}`);
    });

    // ============================================
    // OBSERVER UNTUK MODAL
    // ============================================
    const modalObserver = new MutationObserver(() => {
      if (!canProcess()) return;
      const isActive = checkModalActive();
      if (isActive !== isModalActive) {
        logger.log(`📌 Modal state changed: ${isActive}`);
        setIsModalActive(isActive);
      }
    });

    modalObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
      childList: true,
      subtree: true,
    });
    logger.log("✅ Modal observer started");

    // ============================================
    // OBSERVER KHUSUS UNTUK MODAL CONTENT
    // ============================================
    const contentObserver = new MutationObserver(() => {
      if (!canProcess()) return;

      if (document.visibilityState === "visible") {
        const isActive = checkModalActive();
        setIsModalActive(isActive);

        if (!isActive) {
          setTimeout(() => {
            if (!canProcess()) return;
            if (!checkModalActive()) {
              logger.log("📌 Content mutation trigger");
              triggerProfileUpdate("self");
            }
          }, 300);
        }
      }
    });

    contentObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
    logger.log("✅ Content observer started");

    // ============================================
    // CHANNEL
    // ============================================
    const unsubscribe = authChannel.subscribe((data) => {
      if (!canProcess()) return;
      if (!data || data.from === tabIdRef.current) {
        logger.log(`📨 Message from self, ignoring`);
        return;
      }
      if (data.type === "PROFILE_UPDATE" && !checkModalActive()) {
        logger.log(`📨 Received PROFILE_UPDATE from tab: ${data.from}`);
        triggerProfileUpdate("external");
      }
    });
    logger.log("✅ Channel subscribed");

    // ============================================
    // EVENT LISTENERS
    // ============================================

    // 1. Focusin handler
    const focusinHandler = (event) => {
      if (!canProcess()) return;
      eventCountRef.current.focusin += 1;

      logger.log(`🎯 focusin event #${eventCountRef.current.focusin}`, {
        target: event.target?.tagName || "unknown",
        id: event.target?.id || "no-id",
        className: event.target?.className || "no-class",
      });

      lastFocusTargetRef.current = event.target;
      if (isValidFocusTarget(event.target)) {
        handleTrigger(event);
      }
    };
    handlersRef.current.focusin = focusinHandler;
    document.addEventListener("focusin", focusinHandler, { capture: true });
    logger.log("✅ focusin listener added");

    // 2. Window focus handler
    handlersRef.current.windowFocus = handleWindowFocus;
    window.addEventListener("focus", handleWindowFocus);
    logger.log("✅ window focus listener added");

    // 3. Visibility change handler
    const visibilityHandler = () => {
      if (!canProcess()) return;
      eventCountRef.current.visibility += 1;

      logger.log(
        `👁️ visibilitychange event #${eventCountRef.current.visibility}`,
        {
          state: document.visibilityState,
        },
      );

      if (document.visibilityState === "visible") {
        setTimeout(() => {
          if (!canProcess()) return;

          const activeElement = document.activeElement;
          if (
            (activeElement === document.body ||
              activeElement === document.documentElement ||
              !activeElement) &&
            !checkModalActive()
          ) {
            logger.log("👁️ visibility change trigger");
            triggerProfileUpdate("self");
          }
        }, 300);
      }
    };
    handlersRef.current.visibility = visibilityHandler;
    document.addEventListener("visibilitychange", visibilityHandler);
    logger.log("✅ visibilitychange listener added");

    // ============================================
    // CLEANUP
    // ============================================
    return () => {
      logger.group("🧹 Cleaning up auth focus sync (unmount)", () => {
        logger.log(`📊 Total events:`, {
          focusin: eventCountRef.current.focusin,
          visibility: eventCountRef.current.visibility,
          windowFocus: eventCountRef.current.windowFocus,
        });
      });

      cleanupListeners();
      unsubscribe();
      modalObserver.disconnect();
      contentObserver.disconnect();

      logger.log("✅ All cleanup complete");
    };
  }, [
    dispatch,
    isActive,
    canProcess,
    checkModalActive,
    triggerProfileUpdate,
    handleTrigger,
    handleWindowFocus,
    isValidFocusTarget,
    cleanupListeners,
  ]);

  // ============================================
  // DEBUG: Log state changes
  // ============================================
  useEffect(() => {
    if (DEBUG) {
      logger.log(`📊 State changed - isModalActive: ${isModalActive}`);
    }
  }, [isModalActive]);

  return { isModalActive };
}
